import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, DatePicker, Drawer, Empty, Form, Image, Input, Row, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { saveAs } from 'file-saver';
import { useNavigate, useParams } from 'react-router-dom';
import './Company.scss';
import CompanyService, { CompanyNonconformityGroup, CompanyNonconformityPlan, CompanyNonconformityRow } from '../../services/CompanyService';
import DocumentService from '../../services/DocumentService';
import ProposalService from '../../services/ProposalService';
import { createControlTaskCorrections, deleteControlTaskCorrection } from '../../modules/inspectors/services/controlTaskService';
import { getPlanningInspectorContext, updateInspectionAppointmentLegalProcess } from '../../modules/inspection-appointments/services/inspection-appointmentService';
import { listPublicHolidaysAll } from '../../modules/public-holidays/services/public-holidayService';
import { PageableResponse } from '../../types/pagination';
import { formatDate, normalizeTurkish, openNotification } from '../../utils';
import { buildBlockingPublicHolidayDateSet, buildVisiblePublicHolidayMap } from '../Planning/publicHolidayUtils';
import LegalNotificationPlanTable, { LegalNotificationPlanTableRow } from '../../components/ProjectFollowUp/LegalNotificationPlanTable';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type AttributeOption = {
  id: number;
  name: string;
};

type ProposalOption = {
  id: number;
  proposalNumber: string;
  createdAt?: string | null;
  statusId?: number | null;
  attributes: AttributeOption[];
};

type FilterValues = {
  statusIds?: number[];
  proposalIds?: number[];
  attributeId?: number;
  dateRange?: [Dayjs, Dayjs];
};

type PlanCaseType = 'CORRECTION' | 'FOLLOW_UP';

type PlanFormValues = {
  assignedAccountId?: number;
  plannedRange?: [Dayjs, Dayjs];
  note?: string;
};

type BulkPlanGroup = {
  proposalId: number;
  proposalNumber: string;
  rows: CompanyNonconformityGroup[];
};

type PlannedLegalTableRow = LegalNotificationPlanTableRow & {
  appointmentId: number;
  primaryPlan: CompanyNonconformityPlan;
  plans: CompanyNonconformityPlan[];
};

type PlanningContext = {
  inspectors: any[];
  appointments: any[];
  leaves: any[];
  reservations: any[];
};

type PlanSlotStatus = {
  disabled: boolean;
  reason?: string;
  details?: string[];
};

const emptyPlanningContext: PlanningContext = {
  inspectors: [],
  appointments: [],
  leaves: [],
  reservations: [],
};

const planDayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const planHourSlots = Array.from({ length: 9 }, (_, index) => {
  const hour = 9 + index;
  return {
    hour,
    label: `${String(hour).padStart(2, '0')}:00 - ${String(hour + 1).padStart(2, '0')}:00`,
  };
});

const PROPOSAL_STATUS_OPTIONS = [
  { value: 5, label: 'Kontrol ve Raporlama' },
  { value: 4, label: 'Kontrol Tamamlandı' },
];

const extractProposalAttributes = (proposal: any): AttributeOption[] => {
  const result = new Map<number, AttributeOption>();
  const attributes = Array.isArray(proposal?.attributes) ? proposal.attributes : [];

  attributes.forEach((attribute: any) => {
    const attributeId = Number(attribute?.attributeId ?? attribute?.id);
    if (!Number.isFinite(attributeId) || attributeId <= 0) {
      return;
    }
    const name = String(attribute?.attributeName ?? attribute?.name ?? '').trim() || `Parametre #${attributeId}`;
    result.set(attributeId, { id: attributeId, name });
  });

  return Array.from(result.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
};

const extractProposalOptions = (payload: any): ProposalOption[] => {
  const resolved = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  const result: ProposalOption[] = [];

  resolved.forEach((item: any) => {
      const id = Number(item?.id);
      if (!Number.isFinite(id) || id <= 0) {
        return;
      }
      result.push({
        id,
        proposalNumber: String(item?.proposalNumber ?? `Teklif #${id}`).trim() || `Teklif #${id}`,
        createdAt: item?.createdAt ?? null,
        statusId: Number.isFinite(Number(item?.statusId)) ? Number(item.statusId) : null,
        attributes: extractProposalAttributes(item),
      });
  });

  return result.sort((a, b) => b.proposalNumber.localeCompare(a.proposalNumber, 'tr'));
};

const formatProposalDate = (value?: string | null) => (value ? dayjs(value).format('DD.MM.YYYY') : '-');

const startOfWeekMonday = (value: Dayjs) => {
  const normalizedDay = (value.day() + 6) % 7;
  return value.startOf('day').subtract(normalizedDay, 'day');
};

const formatWeekRange = (weekStart: Dayjs) => `${weekStart.format('DD.MM.YYYY')} - ${weekStart.add(6, 'day').format('DD.MM.YYYY')}`;

const isDateOnlyMoment = (value: Dayjs) => (
  value.hour() === 0 && value.minute() === 0 && value.second() === 0 && value.millisecond() === 0
);

const buildBusyInterval = (
  startInput?: Dayjs | null,
  endInput?: Dayjs | null,
  options?: { allDay?: boolean },
): { start: Dayjs; end: Dayjs } | null => {
  if (!startInput || !dayjs.isDayjs(startInput) || !startInput.isValid()) {
    return null;
  }
  const start = startInput;
  const endCandidate = endInput && dayjs.isDayjs(endInput) && endInput.isValid() ? endInput : null;
  if (options?.allDay) {
    return { start: start.startOf('day'), end: (endCandidate || start).endOf('day') };
  }
  if (endCandidate) {
    if (isDateOnlyMoment(start) && isDateOnlyMoment(endCandidate)) {
      return { start: start.startOf('day'), end: endCandidate.endOf('day') };
    }
    return { start, end: endCandidate.isAfter(start) ? endCandidate : start.add(1, 'minute') };
  }
  return { start, end: isDateOnlyMoment(start) ? start.endOf('day') : start.add(1, 'minute') };
};

const isTimeRangeOverlapping = (startA: Dayjs, endA: Dayjs, startB: Dayjs, endB: Dayjs) => startA.isBefore(endB) && endA.isAfter(startB);

const resolveEntryAccountId = (entry: any): number | null => {
  const value = entry?.accountId ?? entry?.account?.id ?? entry?.inspectorId ?? entry?.inspector?.id ?? entry?.id;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const resolveInspectorOptionLabel = (candidate: any): string => {
  const account = candidate?.account ?? {};
  const parts = [account?.firstName, account?.lastName].filter(Boolean);
  if (parts.length) {
    return parts.join(' ');
  }
  return account?.fullName || account?.name || candidate?.name || `P.K.M. #${candidate?.id}`;
};

const normalizeAttributeId = (value: any): number | null => {
  const raw = typeof value === 'object' && value !== null
    ? value.id ?? value.attributeId
    : value;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const candidateMatchesAllAttributes = (candidate: any, attributeIds: number[]): boolean => {
  if (!attributeIds.length) {
    return true;
  }
  const candidateAttributeIds = new Set((Array.isArray(candidate?.attributes) ? candidate.attributes : [])
    .map(normalizeAttributeId)
    .filter((attributeId: number | null): attributeId is number => attributeId !== null));
  return attributeIds.every((attributeId) => candidateAttributeIds.has(Number(attributeId)));
};

const resolveAppointmentLabel = (appointment: any) => {
  const proposalNumber = appointment?.proposalNumber || appointment?.proposal?.proposalNumber;
  const companyName = appointment?.companyName || appointment?.company?.name;
  return [proposalNumber, companyName].filter(Boolean).join(' / ') || 'Görev';
};

const resolveLeaveLabel = (leave: any) => {
  const type = leave?.type || leave?.leaveType;
  return type ? `İzin (${type})` : 'İzin';
};

const buildReportInfo = (record: Pick<CompanyNonconformityRow, 'reportNumber' | 'reportSequenceNo' | 'responseId'>) => {
  const reportLabel = record.reportNumber || (record.reportSequenceNo ? `${record.reportSequenceNo}. Rapor` : null);
  const responseLabel = record.responseId ? `Yanıt #${record.responseId}` : null;
  return [reportLabel, responseLabel].filter(Boolean).join(' / ');
};

const buildParameterTitle = (record: CompanyNonconformityGroup) => {
  const attributeName = record.attributeName || (record.attributeId ? `Parametre #${record.attributeId}` : '-');
  return record.controlAreaName ? `${attributeName} (${record.controlAreaName})` : attributeName;
};

const buildOrderLabel = (record: Pick<CompanyNonconformityGroup, 'orderNo' | 'reportSequenceNo'>) => {
  const orderNo = record.orderNo ?? record.reportSequenceNo;
  return orderNo ? `${orderNo}. Rapor` : null;
};

const minutesToDays = (minutes: number) => {
  if (!minutes || minutes <= 0) {
    return '0.00';
  }
  return (minutes / 480).toFixed(2);
};

export const buildFilterQueryParams = (filters: FilterValues) => {
  const params: Record<string, any> = {};
  if (filters.proposalIds?.length) {
    params['proposalId.in'] = filters.proposalIds.join(',');
  }
  if (filters.statusIds?.length) {
    params['proposalStatusId.in'] = filters.statusIds.join(',');
  }
  if (filters.attributeId) {
    params['attributeId.equals'] = filters.attributeId;
  }
  if (filters.dateRange?.[0]) {
    params['date.greaterThanOrEqual'] = filters.dateRange[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss');
  }
  if (filters.dateRange?.[1]) {
    params['date.lessThanOrEqual'] = filters.dateRange[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss');
  }
  return params;
};

const renderDistinctiveAnswers = (record: CompanyNonconformityGroup) => {
  const items = Array.isArray(record.distinctiveAnswerItems) ? record.distinctiveAnswerItems : [];
  if (items.length > 0) {
    return (
      <Space direction='vertical' size={0}>
        {items.map((item, index) => (
          <Text type='secondary' key={`${item.questionId ?? index}-${item.questionTitle ?? ''}`}>
            <Text strong type='secondary'>{item.questionTitle || '-'}</Text>
            {`: ${item.answerText || '-'}`}
          </Text>
        ))}
      </Space>
    );
  }
  return record.distinctiveAnswers ? <Text type='secondary'>{record.distinctiveAnswers}</Text> : null;
};

const extractDocumentUrl = (documentPayload: any): string | null =>
  documentPayload?.preSignUrl ??
  documentPayload?.preSignedUrl ??
  documentPayload?.url ??
  documentPayload?.documentUrl ??
  null;

const extractUploadedDocumentUrl = async (uploaded: any): Promise<string | null> => {
  const directUrl = extractDocumentUrl(uploaded?.data ?? uploaded);
  if (directUrl) {
    return directUrl;
  }
  const documentId = uploaded?.data?.id ?? uploaded?.id;
  if (!documentId) {
    return null;
  }
  const response = await DocumentService.getDocument(documentId);
  return extractDocumentUrl(response?.data);
};

const NonconformityImagePreview: React.FC<{ record: CompanyNonconformityRow }> = ({ record }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(record?.imageUrl || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImageUrl(record?.imageUrl || null);

    if (record?.imageUrl || !record?.documentId) {
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    DocumentService.getDocument(record.documentId)
      .then((response: any) => {
        if (!cancelled) {
          setImageUrl(extractDocumentUrl(response?.data));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Uygunsuzluk fotoğrafı yüklenemedi:', error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [record?.documentId, record?.imageUrl]);

  if (loading) {
    return <Text type='secondary'>Yükleniyor</Text>;
  }
  if (!imageUrl) {
    return <Text type='secondary'>-</Text>;
  }
  return (
    <Image
      src={imageUrl}
      alt={record?.imageName || 'Uygunsuzluk fotoğrafı'}
      width={72}
      height={54}
      style={{ objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
      preview={{ mask: 'Büyüt' }}
    />
  );
};

const CompanyNonconformities: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm<FilterValues>();
  const [planForm] = Form.useForm<PlanFormValues>();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<CompanyNonconformityGroup[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState<FilterValues>({});
  const [reportDownloading, setReportDownloading] = useState(false);
  const [proposalOptions, setProposalOptions] = useState<ProposalOption[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<CompanyNonconformityGroup[]>([]);
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
  const [planCaseType, setPlanCaseType] = useState<PlanCaseType>('CORRECTION');
  const [planGroups, setPlanGroups] = useState<BulkPlanGroup[]>([]);
  const [activePlanGroupIndex, setActivePlanGroupIndex] = useState(0);
  const [planning, setPlanning] = useState(false);
  const [planningWeekStart, setPlanningWeekStart] = useState(() => startOfWeekMonday(dayjs()));
  const [planningContext, setPlanningContext] = useState<PlanningContext>(emptyPlanningContext);
  const [planningContextLoading, setPlanningContextLoading] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState<any[]>([]);
  const [publicHolidayLoading, setPublicHolidayLoading] = useState(false);
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);
  const [slotSelectionError, setSlotSelectionError] = useState<string | null>(null);
  const [plansDrawerOpen, setPlansDrawerOpen] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planDeleteKey, setPlanDeleteKey] = useState<string | null>(null);
  const [plans, setPlans] = useState<CompanyNonconformityPlan[]>([]);
  const [legalActionKey, setLegalActionKey] = useState<string | null>(null);
  const [planContractIdDrafts, setPlanContractIdDrafts] = useState<Record<number, string>>({});
  const selectedStatusIds = Form.useWatch('statusIds', form);
  const selectedProposalIds = Form.useWatch('proposalIds', form);
  const selectedPlanAccountId = Form.useWatch('assignedAccountId', planForm);

  useEffect(() => {
    if (!id) {
      setProposalOptions([]);
      return;
    }
    const normalizedStatusIds = Array.isArray(selectedStatusIds)
      ? selectedStatusIds.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
      : [];
    ProposalService.getAllWithoutPagination({
      companyId: id,
      ...(normalizedStatusIds.length ? { statuIds: normalizedStatusIds.join(',') } : {}),
      sort: 'proposalNumber',
      direction: 'DESC',
    })
      .then((response) => setProposalOptions(extractProposalOptions(response)))
      .catch((error) => {
        console.error('Firma teklifleri yüklenemedi:', error);
        setProposalOptions([]);
      });
  }, [id, selectedStatusIds]);

  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      page: pagination.current - 1,
      size: pagination.pageSize,
      ...buildFilterQueryParams(filters),
    };
    return params;
  }, [filters, pagination.current, pagination.pageSize]);

  const reportQueryParams = useMemo(() => buildFilterQueryParams(filters), [filters]);

  const activePlanGroup = planGroups[activePlanGroupIndex] || null;

  const selectedPlanAttributeIds = useMemo(() => {
    const ids = new Set<number>();
    (activePlanGroup?.rows || []).forEach((row) => {
      const attributeId = Number(row.attributeId);
      if (Number.isFinite(attributeId) && attributeId > 0) {
        ids.add(attributeId);
      }
    });
    return Array.from(ids.values());
  }, [activePlanGroup]);

  const inspectorOptions = useMemo(() => {
    return (planningContext.inspectors || [])
      .filter((candidate) => candidateMatchesAllAttributes(candidate, selectedPlanAttributeIds))
      .map((candidate) => {
        const accountId = Number(candidate?.account?.id ?? candidate?.accountId ?? candidate?.id);
        return {
          value: accountId,
          label: resolveInspectorOptionLabel(candidate),
        };
      })
      .filter((option) => Number.isFinite(option.value) && option.value > 0)
      .sort((left, right) => left.label.localeCompare(right.label, 'tr'));
  }, [planningContext.inspectors, selectedPlanAttributeIds]);

  const blockingPublicHolidayDateSet = useMemo(() => buildBlockingPublicHolidayDateSet(publicHolidays), [publicHolidays]);
  const visiblePublicHolidayMap = useMemo(() => buildVisiblePublicHolidayMap(publicHolidays), [publicHolidays]);

  const proposalSelectOptions = useMemo(() => proposalOptions.map((proposal) => {
    const dateLabel = formatProposalDate(proposal.createdAt);
    const searchLabel = `${proposal.proposalNumber} ${dateLabel}`;
    return {
      value: proposal.id,
      searchLabel,
      label: `${proposal.proposalNumber} - ${dateLabel}`,
    };
  }), [proposalOptions]);

  useEffect(() => {
    const selectedIds = form.getFieldValue('proposalIds');
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return;
    }
    const availableProposalIds = new Set(proposalOptions.map((proposal) => proposal.id));
    const nextSelectedIds = selectedIds.filter((proposalId) => availableProposalIds.has(Number(proposalId)));
    if (nextSelectedIds.length !== selectedIds.length) {
      form.setFieldsValue({ proposalIds: nextSelectedIds });
    }
  }, [form, proposalOptions]);

  const attributeOptions = useMemo(() => {
    const selectedIds = Array.isArray(selectedProposalIds) && selectedProposalIds.length > 0
      ? new Set(selectedProposalIds.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0))
      : null;
    const attributesById = new Map<number, AttributeOption>();

    proposalOptions
      .filter((proposal) => !selectedIds || selectedIds.has(proposal.id))
      .forEach((proposal) => {
        proposal.attributes.forEach((attribute) => {
          attributesById.set(attribute.id, attribute);
        });
      });

    return Array.from(attributesById.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [proposalOptions, selectedProposalIds]);

  useEffect(() => {
    const selectedAttributeId = form.getFieldValue('attributeId');
    if (!selectedAttributeId) {
      return;
    }
    const stillAvailable = attributeOptions.some((attribute) => attribute.id === Number(selectedAttributeId));
    if (!stillAvailable) {
      form.setFieldsValue({ attributeId: undefined });
    }
  }, [attributeOptions, form]);

  const fetchRows = useCallback(async () => {
    if (!id) {
      setGroups([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
      return;
    }
    setLoading(true);
    try {
      const response = await CompanyService.getNonconformityGroups(id, queryParams);
      const data = response.data as PageableResponse<CompanyNonconformityGroup>;
      setGroups(Array.isArray(data?.content) ? data.content : []);
      setPagination((prev) => ({
        ...prev,
        total: Number(data?.totalElements ?? 0),
      }));
    } catch (error) {
      console.error('Firma uygunsuzlukları yüklenemedi:', error);
      setGroups([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [id, queryParams]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!planDrawerOpen || !activePlanGroup?.proposalId) {
      return;
    }
    let cancelled = false;
    setPlanningContextLoading(true);
    getPlanningInspectorContext(
      activePlanGroup.proposalId,
      planningWeekStart.format('YYYY-MM-DD'),
      planningWeekStart.add(6, 'day').format('YYYY-MM-DD'),
    )
      .then((data) => {
        if (!cancelled) {
          setPlanningContext({
            inspectors: Array.isArray(data?.inspectors) ? data.inspectors : [],
            appointments: Array.isArray(data?.appointments) ? data.appointments : [],
            leaves: Array.isArray(data?.leaves) ? data.leaves : [],
            reservations: Array.isArray(data?.reservations) ? data.reservations : [],
          });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Planlama uygunluk bilgileri yüklenemedi:', error);
          setPlanningContext(emptyPlanningContext);
          openNotification('error', 'Hata', 'Planlama uygunluk bilgileri yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPlanningContextLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activePlanGroup?.proposalId, planDrawerOpen, planningWeekStart]);

  useEffect(() => {
    if (!planDrawerOpen) {
      return;
    }
    let cancelled = false;
    setPublicHolidayLoading(true);
    listPublicHolidaysAll()
      .then((data) => {
        if (!cancelled) {
          setPublicHolidays(Array.isArray(data) ? data : []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Resmi tatil verileri yüklenemedi:', error);
          setPublicHolidays([]);
          openNotification('error', 'Hata', 'Resmi tatil verileri yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPublicHolidayLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [planDrawerOpen]);

  useEffect(() => {
    const selectedAccountId = Number(planForm.getFieldValue('assignedAccountId'));
    if (!selectedAccountId) {
      return;
    }
    const stillEligible = inspectorOptions.some((option) => Number(option.value) === selectedAccountId);
    if (!stillEligible) {
      planForm.setFieldValue('assignedAccountId', undefined);
      planForm.setFieldValue('plannedRange', undefined);
      setSelectedSlotKey(null);
    }
  }, [inspectorOptions, planForm]);

  const handleFilter = (values: FilterValues) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters(values);
  };

  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters({});
  };

  const handleDownloadReport = async () => {
    if (!id || reportDownloading) {
      return;
    }
    setReportDownloading(true);
    try {
      const response = await CompanyService.downloadNonconformityReportPdf(id, reportQueryParams);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, `firma-uygunsuzluk-raporu-${id}.pdf`);
    } catch (error) {
      console.error('Firma uygunsuzluk raporu oluşturulamadı:', error);
      openNotification('error', 'Hata', 'Uygunsuzluk raporu oluşturulamadı.');
    } finally {
      setReportDownloading(false);
    }
  };

  const isPlanningSourceReady = (row: CompanyNonconformityGroup) => (
    Boolean(row?.responseId)
    && Boolean(row?.taskId)
    && Boolean(row?.attributeId)
    && row?.orderNo != null
    && row?.controlCompleted === true
  );

  const preparePlanFormForGroup = useCallback((group: BulkPlanGroup | null, caseType: PlanCaseType) => {
    const reportLabel = group?.proposalNumber || 'Teklif';
    setPlanningWeekStart(startOfWeekMonday(dayjs()));
    setSelectedSlotKey(null);
    setSlotSelectionError(null);
    setPlanningContext(emptyPlanningContext);
    planForm.setFieldsValue({
      assignedAccountId: undefined,
      plannedRange: undefined,
      note: caseType === 'FOLLOW_UP'
        ? `Takip kontrolü - ${reportLabel}`
        : `Uygunsuzluk düzeltme kontrolü - ${reportLabel}`,
    });
  }, [planForm]);

  const openBulkPlanDrawer = (caseType: PlanCaseType) => {
    if (!selectedRows.length) {
      openNotification('warning', 'Uyarı', 'Planlanacak parametre seçiniz.');
      return;
    }
    const notReady = selectedRows.filter((row) => !isPlanningSourceReady(row));
    if (notReady.length) {
      openNotification('warning', 'Uyarı', 'Yalnızca kontrolü tamamlanmış ve kaynak raporu bulunan parametreler planlanabilir.');
      return;
    }
    const hasOpenPlan = selectedRows.some((row) => (
      caseType === 'FOLLOW_UP' ? row.hasOpenFollowUp : row.hasOpenCorrection
    ));
    if (hasOpenPlan) {
      openNotification(
        'warning',
        'Uyarı',
        caseType === 'FOLLOW_UP'
          ? 'Seçimde açık takip kontrolü planı olan parametre var.'
          : 'Seçimde açık düzeltme planı olan parametre var.',
      );
      return;
    }
    const grouped = new Map<number, BulkPlanGroup>();
    selectedRows.forEach((row) => {
      const proposalId = Number(row.proposalId);
      if (!Number.isFinite(proposalId) || proposalId <= 0) {
        return;
      }
      if (!grouped.has(proposalId)) {
        grouped.set(proposalId, {
          proposalId,
          proposalNumber: row.proposalNumber || `Teklif #${proposalId}`,
          rows: [],
        });
      }
      grouped.get(proposalId)?.rows.push(row);
    });
    const nextGroups = Array.from(grouped.values());
    if (!nextGroups.length) {
      openNotification('warning', 'Uyarı', 'Teklif bilgisi olmayan parametreler planlanamaz.');
      return;
    }
    setPlanCaseType(caseType);
    setPlanGroups(nextGroups);
    setActivePlanGroupIndex(0);
    preparePlanFormForGroup(nextGroups[0], caseType);
    setPlanDrawerOpen(true);
  };

  const closePlanDrawer = () => {
    if (planning) {
      return;
    }
    setPlanDrawerOpen(false);
    setPlanGroups([]);
    setActivePlanGroupIndex(0);
    setSelectedSlotKey(null);
    setSlotSelectionError(null);
    planForm.resetFields();
  };

  const getPlanSlotStatus = useCallback((slotStart: Dayjs, slotEnd: Dayjs): PlanSlotStatus => {
    const accountId = Number(selectedPlanAccountId);
    if (!Number.isFinite(accountId) || accountId <= 0) {
      return { disabled: true, reason: 'P.K.M. seçiniz' };
    }
    const selectedInspector = (planningContext.inspectors || []).find((candidate) => {
      const candidateAccountId = Number(candidate?.account?.id ?? candidate?.accountId ?? candidate?.id);
      return Number.isFinite(candidateAccountId) && candidateAccountId === accountId;
    });
    if (!selectedInspector || !candidateMatchesAllAttributes(selectedInspector, selectedPlanAttributeIds)) {
      return { disabled: true, reason: 'Yetkisiz P.K.M.' };
    }
    if (slotStart.isBefore(dayjs())) {
      return { disabled: true, reason: 'Geçmiş Saat' };
    }
    const dateKey = slotStart.format('YYYY-MM-DD');
    if (blockingPublicHolidayDateSet.has(dateKey)) {
      const holidayNames = (visiblePublicHolidayMap.get(dateKey) || [])
        .filter((holiday: any) => holiday?.halfDay !== true)
        .map((holiday: any) => holiday?.name?.toString().trim())
        .filter(Boolean);
      return { disabled: true, reason: 'Resmi Tatil', details: holidayNames.length ? holidayNames : ['Resmi Tatil'] };
    }
    const overlappingLeave = (planningContext.leaves || []).find((leave) => {
      if (resolveEntryAccountId(leave) !== accountId) {
        return false;
      }
      const leaveStart = dayjs(leave?.startDate);
      const leaveEnd = leave?.endDate ? dayjs(leave.endDate) : leaveStart;
      const interval = buildBusyInterval(leaveStart, leaveEnd, { allDay: true });
      return interval ? isTimeRangeOverlapping(slotStart, slotEnd, interval.start, interval.end) : false;
    });
    if (overlappingLeave) {
      return { disabled: true, reason: 'İzin', details: [resolveLeaveLabel(overlappingLeave)] };
    }
    const overlappingReservation = (planningContext.reservations || []).find((reservation) => {
      if (resolveEntryAccountId(reservation) !== accountId) {
        return false;
      }
      const reservationStartRaw = reservation?.reservationDate ?? reservation?.date ?? reservation?.startDate ?? reservation?.reservationStartDate;
      const reservationStart = dayjs(reservationStartRaw);
      const reservationEndRaw = reservation?.endDate ?? reservation?.reservationEndDate;
      const reservationEnd = reservationEndRaw ? dayjs(reservationEndRaw) : reservationStart;
      const interval = buildBusyInterval(reservationStart, reservationEnd, { allDay: true });
      return interval ? isTimeRangeOverlapping(slotStart, slotEnd, interval.start, interval.end) : false;
    });
    if (overlappingReservation) {
      const note = String(overlappingReservation?.note ?? '').trim();
      return { disabled: true, reason: 'Rezervasyon', details: note ? [note] : ['Rezervasyon'] };
    }
    const overlappingAppointment = (planningContext.appointments || []).find((appointment) => {
      if (resolveEntryAccountId(appointment) !== accountId) {
        return false;
      }
      const appointmentStart = dayjs(appointment?.startDate ?? appointment?.planningAt ?? appointment?.start_date);
      const appointmentEndRaw = appointment?.endDate ?? appointment?.planningEndDate ?? appointment?.end_date;
      const appointmentEnd = appointmentEndRaw ? dayjs(appointmentEndRaw) : appointmentStart;
      const interval = buildBusyInterval(appointmentStart, appointmentEnd);
      return interval ? isTimeRangeOverlapping(slotStart, slotEnd, interval.start, interval.end) : false;
    });
    if (overlappingAppointment) {
      const start = dayjs(overlappingAppointment?.startDate ?? overlappingAppointment?.planningAt ?? overlappingAppointment?.start_date);
      const endRaw = overlappingAppointment?.endDate ?? overlappingAppointment?.planningEndDate ?? overlappingAppointment?.end_date;
      const end = endRaw ? dayjs(endRaw) : null;
      return {
        disabled: true,
        reason: 'Görev',
        details: [
          resolveAppointmentLabel(overlappingAppointment),
          start.isValid() ? `${start.format('HH:mm')} - ${end?.isValid() ? end.format('HH:mm') : '--:--'}` : '',
        ].filter(Boolean),
      };
    }
    return { disabled: false };
  }, [
    blockingPublicHolidayDateSet,
    planningContext.appointments,
    planningContext.inspectors,
    planningContext.leaves,
    planningContext.reservations,
    selectedPlanAccountId,
    selectedPlanAttributeIds,
    visiblePublicHolidayMap,
  ]);

  const selectPlanSlot = (slotStart: Dayjs, slotEnd: Dayjs) => {
    const status = getPlanSlotStatus(slotStart, slotEnd);
    if (status.disabled) {
      return;
    }
    const slotKey = `${slotStart.format('YYYY-MM-DDTHH:mm')}-${slotEnd.format('YYYY-MM-DDTHH:mm')}`;
    setSelectedSlotKey(slotKey);
    setSlotSelectionError(null);
    planForm.setFieldValue('plannedRange', [slotStart, slotEnd]);
  };

  const movePlanningWeek = (deltaWeek: number) => {
    setSelectedSlotKey(null);
    setSlotSelectionError(null);
    planForm.setFieldValue('plannedRange', undefined);
    setPlanningWeekStart((current) => current.add(deltaWeek * 7, 'day'));
  };

  const submitBulkPlan = async () => {
    if (!activePlanGroup || planning) {
      return;
    }
    try {
      await planForm.validateFields();
      const values = planForm.getFieldsValue(true);
      const [plannedStart, plannedEnd] = values.plannedRange || [];
      if (!plannedStart || !plannedEnd) {
        setSlotSelectionError('Tarih ve saat seçiniz.');
        return;
      }
      setPlanning(true);
      await createControlTaskCorrections({
        proposalId: activePlanGroup.proposalId,
        assignedAccountId: values.assignedAccountId,
        caseType: planCaseType,
        plannedStartDate: plannedStart.format('YYYY-MM-DDTHH:mm:ss'),
        plannedEndDate: plannedEnd.format('YYYY-MM-DDTHH:mm:ss'),
        contractId: null,
        contractUrl: null,
        note: values.note || null,
        sources: activePlanGroup.rows.map((row) => ({
          sourceResponseId: row.responseId ?? null,
          sourceTaskId: row.taskId ?? null,
          sourceAttributeId: row.attributeId ?? null,
          sourceOrderNo: row.orderNo ?? null,
        })),
      });
      const nextIndex = activePlanGroupIndex + 1;
      if (nextIndex < planGroups.length) {
        openNotification('success', 'Başarılı', `${activePlanGroup.proposalNumber} için plan oluşturuldu.`);
        setActivePlanGroupIndex(nextIndex);
        preparePlanFormForGroup(planGroups[nextIndex], planCaseType);
        return;
      }
      openNotification('success', 'Başarılı', planCaseType === 'FOLLOW_UP' ? 'Takip kontrolü planlandı.' : 'Düzeltme planı oluşturuldu.');
      setPlanDrawerOpen(false);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setPlanGroups([]);
      void fetchRows();
      if (plansDrawerOpen) {
        void loadPlans();
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Plan oluşturulamadı:', error);
      openNotification('error', 'Hata', error?.message || 'Plan oluşturulamadı.');
    } finally {
      setPlanning(false);
    }
  };

  const loadPlans = useCallback(async () => {
    if (!id) {
      setPlans([]);
      return;
    }
    setPlansLoading(true);
    try {
      const response = await CompanyService.getNonconformityPlans(id, reportQueryParams);
      const nextPlans = Array.isArray(response?.data) ? response.data : [];
      setPlans(nextPlans);
      setPlanContractIdDrafts((prev) => {
        const next = { ...prev };
        nextPlans.forEach((plan) => {
          const appointmentId = Number(plan.appointmentId);
          if (Number.isFinite(appointmentId) && appointmentId > 0 && next[appointmentId] === undefined) {
            next[appointmentId] = String(plan.contractId ?? '');
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Planlanan kontroller yüklenemedi:', error);
      setPlans([]);
      openNotification('error', 'Hata', 'Planlanan kontroller yüklenemedi.');
    } finally {
      setPlansLoading(false);
    }
  }, [id, reportQueryParams]);

  const openPlansDrawer = () => {
    setPlansDrawerOpen(true);
    void loadPlans();
  };

  const applyPlanAppointmentUpdate = (appointmentId: number, updated: any) => {
    setPlans((prev) => prev.map((plan) => {
      if (Number(plan.appointmentId) !== Number(appointmentId)) {
        return plan;
      }
      return {
        ...plan,
        contractId: updated?.contractId ?? plan.contractId,
        contractUrl: updated?.contractUrl ?? plan.contractUrl,
        isCompanyProcessDone: updated?.isCompanyProcessDone ?? plan.isCompanyProcessDone,
        isInspectorProcessDone: updated?.isInspectorProcessDone ?? plan.isInspectorProcessDone,
      };
    }));
    if (Object.prototype.hasOwnProperty.call(updated ?? {}, 'contractId')) {
      setPlanContractIdDrafts((prev) => ({
        ...prev,
        [appointmentId]: String(updated?.contractId ?? ''),
      }));
    }
  };

  const updatePlanLegalProcess = async (plan: CompanyNonconformityPlan, payload: any) => {
    const appointmentId = Number(plan.appointmentId);
    if (!Number.isFinite(appointmentId) || appointmentId <= 0) {
      openNotification('warning', 'Uyarı', 'Plan randevusu bulunamadı.');
      return;
    }
    const actionKey = `${appointmentId}-${Object.keys(payload).join('-')}`;
    setLegalActionKey(actionKey);
    try {
      const updated = await updateInspectionAppointmentLegalProcess(appointmentId, payload);
      applyPlanAppointmentUpdate(appointmentId, updated);
      openNotification('success', 'Başarılı', 'İSG-KATİP süreci güncellendi.');
    } catch (error) {
      console.error('İSG-KATİP süreci güncellenemedi:', error);
      openNotification('error', 'Hata', 'İSG-KATİP süreci güncellenemedi.');
    } finally {
      setLegalActionKey(null);
    }
  };

  const uploadPlanContract = async (plan: CompanyNonconformityPlan, file: any) => {
    const appointmentId = Number(plan.appointmentId);
    if (!Number.isFinite(appointmentId) || appointmentId <= 0) {
      openNotification('warning', 'Uyarı', 'Plan randevusu bulunamadı.');
      return;
    }
    setLegalActionKey(`${appointmentId}-contractUrl`);
    try {
      const uploaded = await DocumentService.postDocument(file, 'appointment_contract');
      const contractUrl = await extractUploadedDocumentUrl(uploaded);
      if (!contractUrl) {
        openNotification('error', 'Hata', 'Sözleşme yükleme başarısız.');
        return;
      }
      const updated = await updateInspectionAppointmentLegalProcess(appointmentId, { contractUrl });
      applyPlanAppointmentUpdate(appointmentId, updated);
      openNotification('success', 'Başarılı', 'Sözleşme yüklendi.');
    } catch (error) {
      console.error('Sözleşme yükleme hatası:', error);
      openNotification('error', 'Hata', 'Sözleşme yükleme başarısız.');
    } finally {
      setLegalActionKey(null);
    }
  };

  const deletePlanGroup = async (row: PlannedLegalTableRow) => {
    const caseIds = row.plans
      .map((plan) => Number(plan.correctionCaseId))
      .filter((caseId) => Number.isFinite(caseId) && caseId > 0);
    if (!caseIds.length) {
      openNotification('warning', 'Uyarı', 'Silinecek plan bulunamadı.');
      return;
    }
    setPlanDeleteKey(row.key);
    try {
      for (const caseId of caseIds) {
        await deleteControlTaskCorrection(caseId);
      }
      openNotification('success', 'Başarılı', 'Plan silindi.');
      await loadPlans();
      void fetchRows();
    } catch (error: any) {
      console.error('Plan silinemedi:', error);
      openNotification('error', 'Hata', error?.message || 'Plan silinemedi.');
    } finally {
      setPlanDeleteKey(null);
    }
  };

  const itemColumns = [
    {
      title: 'Soru',
      key: 'questionTitle',
      render: (_: unknown, record: CompanyNonconformityRow) => (
        <Space direction='vertical' size={0}>
          {record.nodeTitle ? <Text type='secondary'>{record.nodeTitle}</Text> : null}
          <Text strong>{record.questionTitle || '-'}</Text>
        </Space>
      ),
    },
    {
      title: 'Uygunsuz Cevap',
      dataIndex: 'answerTitle',
      key: 'answerTitle',
      width: 180,
      render: (value: string) => value || '-',
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (value: string) => value || '-',
    },
    {
      title: 'Fotoğraf',
      key: 'photo',
      width: 110,
      render: (_: unknown, record: CompanyNonconformityRow) => <NonconformityImagePreview record={record} />,
    },
    {
      title: 'Rapor',
      key: 'report',
      width: 180,
      render: (_: unknown, record: CompanyNonconformityRow) => buildReportInfo(record) || '-',
    },
    {
      title: 'Tarih',
      dataIndex: 'responseUpdatedAt',
      key: 'responseUpdatedAt',
      width: 130,
      render: (value: string) => (value ? formatDate(value) : '-'),
    },
  ];

  const columns = [
    {
      title: 'Teklif',
      key: 'proposal',
      width: 220,
      render: (_: unknown, record: CompanyNonconformityGroup) => (
        <Space direction='vertical' size={0}>
          <Text strong>{record.proposalNumber || '-'}</Text>
          <Text type='secondary'>{formatProposalDate(record.proposalCreatedAt)}</Text>
        </Space>
      ),
    },
    {
      title: 'Parametre',
      key: 'attributeName',
      render: (_: unknown, record: CompanyNonconformityGroup) => (
        <Space direction='vertical' size={0}>
          <Text strong>
            {buildParameterTitle(record)}
            {buildOrderLabel(record) ? ` - ${buildOrderLabel(record)}` : ''}
          </Text>
          {renderDistinctiveAnswers(record)}
          {record.responseId ? <Text type='secondary'>{`Yanıt #${record.responseId}`}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Uygunsuzluk Sayısı',
      dataIndex: 'nonconformityCount',
      key: 'nonconformityCount',
      width: 180,
      render: (value: number) => value ?? 0,
    },
    {
      title: 'P.K.M Uzmanı',
      dataIndex: 'inspectorName',
      key: 'inspectorName',
      width: 160,
      render: (value: string) => value || '-',
    },
    {
      title: 'Tarih',
      dataIndex: 'responseUpdatedAt',
      key: 'responseUpdatedAt',
      width: 130,
      render: (value: string) => (value ? formatDate(value) : '-'),
    },
  ];

  const selectedHasOpenCorrection = selectedRows.some((row) => row.hasOpenCorrection);
  const selectedHasOpenFollowUp = selectedRows.some((row) => row.hasOpenFollowUp);
  const planGridLoading = planningContextLoading || publicHolidayLoading;
  const planningWeekDays = Array.from({ length: 7 }, (_, index) => planningWeekStart.add(index, 'day'));
  const selectedPlanInspectorLabel = inspectorOptions.find((option) => Number(option.value) === Number(selectedPlanAccountId))?.label || '-';
  const activePlanSummaryRows = Array.from((activePlanGroup?.rows || []).reduce((map, row) => {
    const key = `${row.attributeId ?? 'no-attribute'}-${row.controlAreaName ?? ''}`;
    const existing = map.get(key) || {
      key,
      name: buildParameterTitle(row),
      count: 0,
    };
    existing.count += Number(row.nonconformityCount ?? 0);
    map.set(key, existing);
    return map;
  }, new Map<string, { key: string; name: string; count: number }>()).values());

  const plannedLegalRows = useMemo<PlannedLegalTableRow[]>(() => {
    const grouped = new Map<string, CompanyNonconformityPlan[]>();
    plans.forEach((plan) => {
      const appointmentId = Number(plan.appointmentId);
      const accountId = Number(plan.assignedAccountId);
      const key = Number.isFinite(appointmentId) && appointmentId > 0
        ? `${appointmentId}-${Number.isFinite(accountId) ? accountId : 'no-account'}`
        : `case-${plan.correctionCaseId ?? plan.sourceResponseId ?? grouped.size}`;
      const current = grouped.get(key) || [];
      current.push(plan);
      grouped.set(key, current);
    });

    return Array.from(grouped.entries()).map(([key, groupedPlans]) => {
      const sortedPlans = [...groupedPlans].sort((a, b) => {
        const startA = a.plannedStartDate ? dayjs(a.plannedStartDate).valueOf() : 0;
        const startB = b.plannedStartDate ? dayjs(b.plannedStartDate).valueOf() : 0;
        return startA - startB;
      });
      const primaryPlan = sortedPlans[0];
      const appointmentId = Number(primaryPlan.appointmentId);
      const hasAppointment = Number.isFinite(appointmentId) && appointmentId > 0;
      const inspectorId = Number(primaryPlan.assignedAccountId);
      const categories = new Map<string, { key: string; name: string; value: number }>();
      const efforts = new Map<string, number>();

      sortedPlans.forEach((plan) => {
        const groupName = String(plan.sourceGroupName || 'Kategori Yok').trim() || 'Kategori Yok';
        const groupKey = plan.sourceGroupId != null ? `g_${plan.sourceGroupId}` : `group-${groupName}`;
        const rawQuestionCount = Number(plan.questionCount ?? 0);
        const questionCount = Number.isFinite(rawQuestionCount) && rawQuestionCount > 0 ? rawQuestionCount : 0;
        const categoryValue = plan.sourceCountable === false ? (questionCount > 0 ? 1 : 0) : questionCount;
        const currentCategory = categories.get(groupKey) || { key: groupKey, name: groupName, value: 0 };
        currentCategory.value += categoryValue;
        categories.set(groupKey, currentCategory);

        const minutesPerUnit = Number(plan.sourceFieldControlTime ?? 0);
        if (Number.isFinite(minutesPerUnit) && minutesPerUnit > 0 && categoryValue > 0) {
          const offeringName = String(plan.sourceOfferingName || 'Diğer').trim() || 'Diğer';
          efforts.set(offeringName, (efforts.get(offeringName) || 0) + (categoryValue * minutesPerUnit));
        }
      });

      const caseTypes = Array.from(new Set(sortedPlans.map((plan) => plan.caseType).filter(Boolean))) as string[];
      const isCorrectionOnly = caseTypes.length > 0 && caseTypes.every((caseType) => caseType === 'CORRECTION');
      const statuses = Array.from(new Set(sortedPlans.map((plan) => plan.status).filter(Boolean))) as string[];
      const reportLabels = Array.from(new Set(sortedPlans
        .map((plan) => plan.sourceReportNumber || (plan.sourceReportSequenceNo ? `${plan.sourceReportSequenceNo}. Rapor` : null))
        .filter(Boolean) as string[]));
      const attributeLabels = Array.from(new Set(sortedPlans
        .map((plan) => plan.sourceAttributeName || (plan.sourceAttributeId ? `Parametre #${plan.sourceAttributeId}` : null))
        .filter(Boolean) as string[]));

      const plannedDateText = primaryPlan.plannedStartDate
        ? `${formatDate(primaryPlan.plannedStartDate)} ${dayjs(primaryPlan.plannedStartDate).format('HH:mm')}${primaryPlan.plannedEndDate ? ` - ${dayjs(primaryPlan.plannedEndDate).format('HH:mm')}` : ''}`
        : null;
      const effortParts = Array.from(efforts.entries())
        .filter(([, minutes]) => minutes > 0)
        .sort(([nameA], [nameB]) => nameA.localeCompare(nameB, 'tr'))
        .map(([name, minutes]) => `${name}: ${minutesToDays(minutes)} gün`);

      return {
        key,
        appointmentId: hasAppointment ? appointmentId : 0,
        inspectorId: Number.isFinite(inspectorId) ? inspectorId : 0,
        inspectorName: primaryPlan.assignedInspectorName || '-',
        dateRangeText: plannedDateText,
        effortParts,
        contractId: primaryPlan.contractId,
        contractIdDraft: hasAppointment ? planContractIdDrafts[appointmentId] ?? primaryPlan.contractId ?? '' : '',
        contractUrl: primaryPlan.contractUrl,
        isCompanyProcessDone: primaryPlan.isCompanyProcessDone,
        isInspectorProcessDone: primaryPlan.isInspectorProcessDone,
        categories: Array.from(categories.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr')),
        tags: (
          <Space wrap size={4}>
            {caseTypes.map((caseType) => (
              <Tag key={`type-${caseType}`} color={caseType === 'FOLLOW_UP' ? 'geekblue' : 'purple'}>
                {caseType === 'FOLLOW_UP' ? 'Takip Kontrolü' : 'Düzeltme'}
              </Tag>
            ))}
            {statuses.map((status) => <Tag key={`status-${status}`}>{status}</Tag>)}
          </Space>
        ),
        detailLines: [
          <Text strong key="proposal">{primaryPlan.proposalNumber || '-'}</Text>,
          attributeLabels.length ? <Text type="secondary" key="attributes">{attributeLabels.join(', ')}</Text> : null,
          reportLabels.length ? <Text type="secondary" key="reports">{reportLabels.join(', ')}</Text> : null,
          !hasAppointment ? <Text type="secondary" key="no-appointment">Plan randevusu yok.</Text> : null,
        ].filter(Boolean) as React.ReactNode[],
        contractActionLoading: hasAppointment && legalActionKey === `${appointmentId}-contractUrl`,
        companySwitchLoading: hasAppointment && legalActionKey === `${appointmentId}-isCompanyProcessDone`,
        inspectorSwitchLoading: hasAppointment && legalActionKey === `${appointmentId}-isInspectorProcessDone`,
        contractIdDisabled: !hasAppointment,
        disabled: !hasAppointment,
        deleteLoading: planDeleteKey === key,
        showDeletePlan: true,
        hideContractProcess: isCorrectionOnly,
        hideLegalNotificationDetails: isCorrectionOnly,
        primaryPlan,
        plans: sortedPlans,
      };
    });
  }, [plans, planContractIdDrafts, legalActionKey, planDeleteKey]);

  return (
    <Card className='br-30px kontrolx-card'>
      <Row align='middle' justify='space-between' gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Firma Uygunsuzlukları</Typography.Title>
        <Button onClick={() => navigate(`/companies/${id}`)}>Firma Detayına Dön</Button>
      </Row>
      <Form form={form} layout='inline' onFinish={handleFilter} style={{ marginBottom: 16, rowGap: 12 }}>
        <Form.Item name='statusIds'>
          <Select
            allowClear
            mode='multiple'
            showSearch
            maxTagCount='responsive'
            placeholder='Statü'
            style={{ width: 260 }}
            optionFilterProp='label'
            filterOption={(input, option) =>
              normalizeTurkish(String(option?.label ?? '')).includes(normalizeTurkish(input ?? ''))
            }
            options={PROPOSAL_STATUS_OPTIONS}
          />
        </Form.Item>
        <Form.Item name='proposalIds'>
          <Select
            allowClear
            mode='multiple'
            showSearch
            maxTagCount='responsive'
            placeholder='Teklif'
            style={{ width: 320 }}
            optionFilterProp='searchLabel'
            filterOption={(input, option) =>
              normalizeTurkish(String((option as any)?.searchLabel ?? '')).includes(normalizeTurkish(input ?? ''))
            }
            options={proposalSelectOptions}
          />
        </Form.Item>
        <Form.Item name='attributeId'>
          <Select
            allowClear
            showSearch
            placeholder='Parametre'
            style={{ width: 240 }}
            optionFilterProp='label'
            filterOption={(input, option) =>
              normalizeTurkish(String(option?.label ?? '')).includes(normalizeTurkish(input ?? ''))
            }
            options={attributeOptions.map((attribute) => ({
              value: attribute.id,
              label: attribute.name,
            }))}
          />
        </Form.Item>
        <Form.Item name='dateRange'>
          <RangePicker format='DD.MM.YYYY' placeholder={['Başlangıç', 'Bitiş']} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type='primary' htmlType='submit'>Filtrele</Button>
            <Button onClick={handleDownloadReport} loading={reportDownloading}>Rapor Oluştur</Button>
            <Button onClick={handleReset}>Temizle</Button>
          </Space>
        </Form.Item>
      </Form>
      <Row justify='space-between' align='middle' style={{ marginBottom: 12 }} gutter={[12, 12]}>
        <Space wrap className='company-nonconformity-action-buttons'>
          <Button
            className='company-nonconformity-action-button'
            disabled={!selectedRows.length || selectedHasOpenCorrection}
            onClick={() => openBulkPlanDrawer('CORRECTION')}
          >
            Düzeltme Planla
          </Button>
          <Button
            className='company-nonconformity-action-button'
            disabled={!selectedRows.length || selectedHasOpenFollowUp}
            onClick={() => openBulkPlanDrawer('FOLLOW_UP')}
          >
            Takip Kontrolü Planla
          </Button>
          <Button className='company-nonconformity-action-button' onClick={openPlansDrawer}>
            Planlanan Kontrol ve Düzeltmeler
          </Button>
        </Space>
        <Text type='secondary'>{selectedRows.length ? `${selectedRows.length} parametre seçildi` : 'Parametre seçiniz'}</Text>
      </Row>
      <Table
        rowKey={(record) => record.groupKey}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows as CompanyNonconformityGroup[]);
          },
          getCheckboxProps: (record) => ({
            disabled: !isPlanningSourceReady(record),
          }),
        }}
        columns={columns as any}
        dataSource={groups}
        loading={loading}
        locale={{ emptyText: 'Uygunsuzluk bulunamadı.' }}
        expandable={{
          expandedRowRender: (record) => (
            <Table
              rowKey={(item, index) => `${record.groupKey}-${item.questionId}-${item.answerTitle}-${index}`}
              columns={itemColumns as any}
              dataSource={record.items || []}
              pagination={false}
              size='small'
              locale={{ emptyText: 'Uygunsuzluk bulunamadı.' }}
            />
          ),
          rowExpandable: (record) => Boolean(record.items?.length),
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize,
            }));
          },
        }}
      />
      <Drawer
        title={planCaseType === 'FOLLOW_UP' ? 'Takip Kontrolü Planı' : 'Düzeltme Planı'}
        open={planDrawerOpen}
        onClose={closePlanDrawer}
        width={1080}
        destroyOnClose
        extra={(
          <Space>
            <Text type='secondary'>
              {planGroups.length > 1 ? `${activePlanGroupIndex + 1}/${planGroups.length} teklif` : null}
            </Text>
            <Button onClick={closePlanDrawer}>İptal</Button>
            <Button type='primary' loading={planning} onClick={submitBulkPlan}>
              {activePlanGroupIndex + 1 < planGroups.length ? 'Kaydet ve Sonraki Teklife Geç' : 'Kaydet'}
            </Button>
          </Space>
        )}
      >
        {activePlanGroup ? (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            <Alert
              type='info'
              showIcon
              message={`${activePlanGroup.proposalNumber} için ${activePlanGroup.rows.length} parametre planlanacak.`}
            />
            <Form form={planForm} layout='vertical'>
              <Form.Item name='assignedAccountId' label='P.K.M. Uzmanı' rules={[{ required: true, message: 'P.K.M. uzmanı seçiniz.' }]}>
                <Select
                  showSearch
                  placeholder='P.K.M. uzmanı seçiniz'
                  options={inspectorOptions}
                  loading={planningContextLoading}
                  notFoundContent={planningContextLoading ? 'Yükleniyor...' : 'Uygun P.K.M. uzmanı bulunamadı'}
                  onChange={() => {
                    setSelectedSlotKey(null);
                    setSlotSelectionError(null);
                    planForm.setFieldValue('plannedRange', undefined);
                  }}
                  filterOption={(input, option) =>
                    normalizeTurkish(String(option?.label ?? '')).includes(normalizeTurkish(input ?? ''))
                  }
                />
              </Form.Item>
              <Form.Item
                label='Müsait Gün / Saat'
                validateStatus={slotSelectionError ? 'error' : undefined}
                help={slotSelectionError || undefined}
              >
                <Space direction='vertical' size={8} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <Button onClick={() => movePlanningWeek(-1)}>Önceki Hafta</Button>
                    <Text strong>{formatWeekRange(planningWeekStart)}</Text>
                    <Button onClick={() => movePlanningWeek(1)}>Sonraki Hafta</Button>
                  </div>
                  {planGridLoading ? <Text type='secondary'>Uygunluk bilgileri yükleniyor...</Text> : null}
                  <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 8 }}>
                    <table style={{ width: '100%', minWidth: 860, borderCollapse: 'separate', borderSpacing: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 112, padding: '10px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'left', background: '#fafafa' }}>
                            Saat
                          </th>
                          {planningWeekDays.map((day, index) => {
                            const dateKey = day.format('YYYY-MM-DD');
                            const isHoliday = blockingPublicHolidayDateSet.has(dateKey);
                            return (
                              <th
                                key={dateKey}
                                style={{
                                  minWidth: 104,
                                  padding: '10px 8px',
                                  borderBottom: '1px solid #f0f0f0',
                                  textAlign: 'center',
                                  background: isHoliday ? '#fff1f0' : '#fafafa',
                                  color: isHoliday ? '#cf1322' : undefined,
                                }}
                              >
                                <div>{planDayNames[index]}</div>
                                <div style={{ fontSize: 12, fontWeight: 400 }}>{day.format('DD.MM')}</div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {planHourSlots.map((hourSlot) => (
                          <tr key={hourSlot.hour}>
                            <td style={{ padding: 8, borderBottom: '1px solid #f5f5f5', background: '#fff', fontWeight: 600 }}>
                              {hourSlot.label}
                            </td>
                            {planningWeekDays.map((day) => {
                              const slotStart = day.hour(hourSlot.hour).minute(0).second(0).millisecond(0);
                              const slotEnd = slotStart.add(1, 'hour');
                              const dateKey = day.format('YYYY-MM-DD');
                              const slotKey = `${slotStart.format('YYYY-MM-DDTHH:mm')}-${slotEnd.format('YYYY-MM-DDTHH:mm')}`;
                              const status = planGridLoading ? { disabled: true, reason: 'Yükleniyor' } : getPlanSlotStatus(slotStart, slotEnd);
                              const isSelected = selectedSlotKey === slotKey;
                              const details = status.details?.filter(Boolean).join(' / ');
                              const tooltipTitle = status.reason
                                ? [status.reason, details].filter(Boolean).join(' - ')
                                : `${slotStart.format('DD.MM.YYYY HH:mm')} - ${slotEnd.format('HH:mm')}`;
                              return (
                                <td key={`${dateKey}-${hourSlot.hour}`} style={{ padding: 6, borderBottom: '1px solid #f5f5f5' }}>
                                  <Tooltip title={tooltipTitle}>
                                    <span style={{ display: 'block' }}>
                                      <Button
                                        block
                                        size='small'
                                        type={isSelected ? 'primary' : 'default'}
                                        disabled={status.disabled}
                                        onClick={() => selectPlanSlot(slotStart, slotEnd)}
                                        style={{
                                          minHeight: 42,
                                          height: 'auto',
                                          whiteSpace: 'normal',
                                          padding: '5px 6px',
                                          opacity: status.disabled ? 0.72 : 1,
                                        }}
                                      >
                                        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                          <span>{slotStart.format('HH:mm')} - {slotEnd.format('HH:mm')}</span>
                                          {status.reason ? <span style={{ fontSize: 11 }}>{status.reason}</span> : null}
                                        </span>
                                      </Button>
                                    </span>
                                  </Tooltip>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!selectedPlanAccountId ? <Text type='secondary'>P.K.M. uzmanı seçildikten sonra uygun saat seçilebilir.</Text> : null}
                </Space>
              </Form.Item>
              {selectedPlanAccountId ? (
                <Table
                  size='small'
                  pagination={false}
                  rowKey='key'
                  dataSource={[{ key: 'summary' }]}
                  columns={[
                    {
                      title: 'P.K.M. Uzmanı',
                      key: 'inspector',
                      width: 360,
                      render: () => (
                        <Space direction='vertical' size={4}>
                          <Text strong>{selectedPlanInspectorLabel}</Text>
                          <Text type='secondary'>{activePlanGroup.proposalNumber}</Text>
                        </Space>
                      ),
                    },
                    {
                      title: 'Yasal Bildirim Detayları',
                      key: 'details',
                      render: () => (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            {activePlanSummaryRows.map((summary) => (
                              <tr key={summary.key}>
                                <td style={{ padding: '4px 12px 4px 0', fontWeight: 500 }}>{summary.name}</td>
                                <td style={{ padding: '4px 0', textAlign: 'right', minWidth: 40 }}>{summary.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ),
                    },
                  ] as any}
                />
              ) : null}
              <Form.Item name='note' label='Not'>
                <Input.TextArea rows={3} />
              </Form.Item>
            </Form>
          </Space>
        ) : (
          <Empty description='Planlanacak teklif bulunamadı.' />
        )}
      </Drawer>
      <Drawer
        title='Planlanan Kontrol ve Düzeltmeler'
        open={plansDrawerOpen}
        onClose={() => setPlansDrawerOpen(false)}
        width={1200}
        destroyOnClose
        extra={<Button onClick={loadPlans} loading={plansLoading}>Yenile</Button>}
      >
        <LegalNotificationPlanTable
          loading={plansLoading}
          rows={plannedLegalRows}
          emptyText={<Empty description='Planlanan kontrol veya düzeltme bulunamadı.' />}
          onUploadFile={(row, file) => {
            const plannedRow = row as PlannedLegalTableRow;
            void uploadPlanContract(plannedRow.primaryPlan, file);
          }}
          onContractIdChange={(row, value) => {
            const plannedRow = row as PlannedLegalTableRow;
            if (!plannedRow.appointmentId) {
              return;
            }
            setPlanContractIdDrafts((prev) => ({
              ...prev,
              [plannedRow.appointmentId]: value,
            }));
          }}
          onContractIdBlur={(row, value) => {
            const plannedRow = row as PlannedLegalTableRow;
            const nextValue = value.trim();
            if (nextValue !== String(plannedRow.primaryPlan.contractId ?? '').trim()) {
              void updatePlanLegalProcess(plannedRow.primaryPlan, { contractId: nextValue });
            }
          }}
          onCompanyProcessChange={(row, checked) => {
            const plannedRow = row as PlannedLegalTableRow;
            void updatePlanLegalProcess(plannedRow.primaryPlan, { isCompanyProcessDone: checked });
          }}
          onInspectorProcessChange={(row, checked) => {
            const plannedRow = row as PlannedLegalTableRow;
            void updatePlanLegalProcess(plannedRow.primaryPlan, { isInspectorProcessDone: checked });
          }}
          onDeletePlan={(row) => {
            void deletePlanGroup(row as PlannedLegalTableRow);
          }}
        />
      </Drawer>
    </Card>
  );
};

export default CompanyNonconformities;
